import { useCallback, useEffect, useRef, useState } from 'react'
import { retryDelayMs } from 'utils/gatewayRetry'
import {
  getImageSources,
  isImageUrl,
  isVideoUrl,
  OptimizedImageType
} from 'utils/imageOptimizer'

export enum ContentType {
  AUDIO = 'AUDIO',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO'
}

const fetchContentType = async (url: string): Promise<string | null> => {
  const response = await fetch(url, { method: 'HEAD' })
  return response.headers.get('content-type')
}

const toContentType = (headerValue: string | null): ContentType => {
  if (headerValue?.startsWith('audio')) {
    return ContentType.AUDIO
  }
  if (headerValue?.startsWith('video')) {
    return ContentType.VIDEO
  }
  return ContentType.IMAGE
}

type Outcome = 'loading' | 'video' | 'failed'

interface LoadState {
  attempt: number
  loadId: number
  outcome: Outcome
  rawUrl: string | undefined
}

let lastLoadId = 0

// each load gets its own id, so a late timer or HEAD result of an earlier
// load of the same URL is ignored
const initialState = (rawUrl: string | undefined): LoadState => {
  lastLoadId += 1
  return {
    attempt: 0,
    loadId: lastLoadId,
    outcome: rawUrl && isVideoUrl(rawUrl) ? 'video' : 'loading',
    rawUrl
  }
}

/**
 * Image URL to render for a media URL. A failed image load tries the next
 * source (resized, resized again, source URL) after a random wait. When every
 * source fails, a HEAD request tells whether the URL is a video; otherwise
 * the placeholder shows.
 */
const useOptimizedImageUrl = (
  type: OptimizedImageType,
  rawUrl?: string
): {
  contentType: ContentType
  imageUrl: string | undefined
  onImageLoadFailed: () => void
  onVideoLoadFailed: () => void
  showPlaceholder: boolean
} => {
  const [storedState, setState] = useState(() => initialState(rawUrl))
  const state =
    storedState.rawUrl === rawUrl ? storedState : initialState(rawUrl)
  if (state !== storedState) {
    setState(state)
  }
  const sources = getImageSources(rawUrl, type)

  const retryTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(retryTimer.current), [rawUrl])

  const updateFor = useCallback(
    (loadId: number, update: Partial<LoadState>) =>
      setState((current) =>
        current.loadId === loadId ? { ...current, ...update } : current
      ),
    []
  )

  const onImageLoadFailed = useCallback(() => {
    if (!rawUrl) {
      return
    }
    const { loadId } = state
    const nextAttempt = state.attempt + 1
    if (nextAttempt < sources.length) {
      retryTimer.current = setTimeout(
        () => updateFor(loadId, { attempt: nextAttempt }),
        retryDelayMs(state.attempt)
      )
      return
    }
    fetchContentType(rawUrl)
      .then((headerValue) =>
        updateFor(loadId, {
          outcome:
            toContentType(headerValue) === ContentType.VIDEO
              ? 'video'
              : 'failed'
        })
      )
      .catch((error) => {
        console.error(`Failed to load media ${rawUrl}`, error)
        updateFor(loadId, { outcome: 'failed' })
      })
  }, [rawUrl, sources.length, state, updateFor])

  const onVideoLoadFailed = useCallback(
    () => updateFor(state.loadId, { outcome: 'failed' }),
    [state.loadId, updateFor]
  )

  const isVideo = state.outcome === 'video'
  return {
    contentType: isVideo ? ContentType.VIDEO : ContentType.IMAGE,
    imageUrl: isVideo ? rawUrl : sources[state.attempt],
    onImageLoadFailed,
    onVideoLoadFailed,
    showPlaceholder: state.outcome === 'failed'
  }
}

/**
 * Content type of an animation URL: from its extension when it has a known
 * one, else from a HEAD request.
 */
export const useAnimationContentType = (url?: string): ContentType => {
  const [probed, setProbed] = useState<{ type: ContentType; url: string }>()
  const knownType =
    !url || isImageUrl(url) || url.endsWith('.glb')
      ? ContentType.IMAGE
      : isVideoUrl(url)
      ? ContentType.VIDEO
      : undefined

  useEffect(() => {
    if (!url || knownType) {
      return
    }
    fetchContentType(url)
      .then((headerValue) =>
        setProbed({ type: toContentType(headerValue), url })
      )
      .catch((error) => console.error(`Failed to probe ${url}`, error))
  }, [url, knownType])

  if (knownType) {
    return knownType
  }
  return probed && probed.url === url ? probed.type : ContentType.IMAGE
}

export default useOptimizedImageUrl
